import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Loader2, Building2, Search } from "lucide-react";

// Shared French company auto-search (SIREN/SIRET) used for professional clients
// in the Clients module, the Calendar quick-create, and the client edit form.
// Fills raison sociale, SIREN, SIRET and address; all fields stay editable.
export default function CompanySearchField({ value, onChange, inputClass = "bg-slate-50 border-slate-200", testidPrefix = "company", publicSearch = false }) {
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [manual, setManual] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const boxRef = useRef(null);
  const q = value.company || "";

  useEffect(() => {
    if (!openList || manual) return undefined;
    const t = setTimeout(async () => {
      if ((q || "").trim().length < 3) { setResults([]); return; }
      setLoading(true); setFailed(false);
      try {
        const { data } = await api.get(publicSearch ? "/public/company-search" : "/company-search", { params: { q } });
        setResults(data.results || []);
      } catch { setResults([]); setFailed(true); } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, openList, manual, publicSearch]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const composeAddress = (e) => {
    if (e.address && /\d{5}/.test(e.address)) return e.address;
    const cpVille = [e.postal_code, e.city].filter(Boolean).join(" ");
    return [e.address, cpVille].filter(Boolean).join(", ");
  };

  const applyEtab = (r, e) => {
    onChange({ company: r.name || q, siren: r.siren || "", siret: e.siret || "", address: composeAddress(e) });
    setOpenList(false); setExpanded(null); setResults([]);
  };

  const pick = (r) => {
    const etabs = r.etablissements || [];
    if (etabs.length <= 1) applyEtab(r, etabs[0] || {});
    else { onChange({ company: r.name || q, siren: r.siren || "" }); setExpanded(expanded === r.siren ? null : r.siren); }
  };

  return (
    <div className="col-span-2 space-y-3" ref={boxRef}>
      <div className="space-y-1.5 relative">
        <Label className="text-xs text-slate-500">{manual ? "Nom de l'entreprise *" : "Société * — recherche par nom, SIREN (9 chiffres) ou SIRET (14 chiffres)"}</Label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            data-testid={`${testidPrefix}-search-input`}
            value={q}
            onChange={(e) => { onChange({ company: e.target.value }); if (!openList) setOpenList(true); }}
            onFocus={() => setOpenList(true)}
            placeholder="Ex : Pronost garage…"
            className={`pl-9 ${inputClass}`}
            autoComplete="off"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
        </div>
        {openList && !manual && (results.length > 0 || (q.trim().length >= 3 && !loading)) && (
          <div data-testid={`${testidPrefix}-suggestions`} className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
            {results.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500" data-testid={`${testidPrefix}-no-result`}>{failed ? "Recherche indisponible pour le moment." : "Aucune entreprise trouvée."} Le nom saisi est conservé.</div>
            ) : results.map((r) => (
              <div key={r.siren} className="border-b border-slate-100 last:border-0">
                <button type="button" onClick={() => pick(r)} data-testid={`${testidPrefix}-suggestion-${r.siren}`}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-500/5">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium text-slate-900 truncate">{r.name}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 ml-5">
                    SIREN {r.siren}{r.etablissement_count ? ` · ${r.etablissement_count} établissement(s)` : ""}{(r.etablissements || []).length > 1 ? " — cliquez pour choisir l'établissement" : ""}
                  </div>
                </button>
                {expanded === r.siren && (
                  <div className="bg-slate-50/70">
                    {(r.etablissements || []).map((e) => (
                      <button key={e.siret} type="button" onClick={() => applyEtab(r, e)} data-testid={`${testidPrefix}-etab-${e.siret}`}
                        className="w-full text-left px-3 py-2 pl-9 hover:bg-emerald-500/10 border-t border-slate-100">
                        <div className="text-xs font-medium text-slate-800">{e.is_siege ? "Siège · " : ""}SIRET {e.siret}</div>
                        <div className="text-[11px] text-slate-500">{[e.address, [e.postal_code, e.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "Adresse non disponible"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button type="button" data-testid={`${testidPrefix}-manual`} onClick={() => { setManual(true); setOpenList(false); }} className="w-full text-left px-3 py-2.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 border-t border-slate-100">Mon entreprise n'apparaît pas — saisir mes informations manuellement</button>
          </div>
        )}
        {manual && <button type="button" data-testid={`${testidPrefix}-back-search`} onClick={() => setManual(false)} className="text-[11px] text-emerald-700 underline">Revenir à la recherche automatique</button>}
      </div>
      {manual && <div className="space-y-1.5"><Label className="text-xs text-slate-500">Adresse de l'entreprise</Label><Input data-testid={`${testidPrefix}-address`} value={value.address || ""} onChange={(e) => onChange({ address: e.target.value })} placeholder="N°, rue, code postal, ville" className={inputClass} /></div>}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">SIREN {manual ? "(si connu)" : ""}</Label>
          <Input data-testid={`${testidPrefix}-siren`} value={value.siren || ""} onChange={(e) => onChange({ siren: e.target.value })} placeholder="9 chiffres" className={inputClass} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">SIRET (établissement)</Label>
          <Input data-testid={`${testidPrefix}-siret`} value={value.siret || ""} onChange={(e) => onChange({ siret: e.target.value })} placeholder="14 chiffres" className={inputClass} />
        </div>
      </div>
    </div>
  );
}
